/**
 * BoardPage - Página principal do Kanban Board
 */
import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { usePrazosBoardData } from '@context/PrazosContext'
import { useAuth } from '@context/AuthContext'
import { useNotification } from '@context/NotificationContext'
import { usePageHeaderActions } from '@context/PageHeaderContext'
import { APP_CONFIG } from '@config/constants'
import FiltersService from '@services/filters'
import AuthService from '@services/auth'
import ApiService from '@services/api'
import { parseStatus } from '@services/prazoStatus'
import KanbanColumn from './KanbanColumn'
import PrazoModal from './PrazoModal'
import ImportModal from './ImportModal'
import ProcessDocumentsModal from './ProcessDocumentsModal'
import PjeQuickCopyButton from '@components/pje/PjeQuickCopyButton'
import { ConfirmModal } from '@components/shared/Modal'

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function isJudicialPrazo(prazo) {
  const tipoAgenda = normalizeText(prazo.tipoAgenda || prazo.tipo_agenda || prazo.tipo_Agenda)
  return tipoAgenda === 'prazo'
}

export default function BoardPage() {
  const prazosCtx = usePrazosBoardData()
  const notify = useNotification()
  const setPageHeaderActions = usePageHeaderActions()
  const { canManageBoard, canViewAgenda, isAdmin, canAccessBoard } = useAuth()

  const [searchTerm, setSearchTerm] = useState('')
  const [areaFilter, setAreaFilter] = useState('')
  const [responsavelFilter, setResponsavelFilter] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [showPrazoModal, setShowPrazoModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showConfirmDelete, setShowConfirmDelete] = useState(null)
  const [editingPrazo, setEditingPrazo] = useState(null)
  const [activeTab, setActiveTab] = useState('judiciais')
  const [documentsProcess, setDocumentsProcess] = useState('')
  const closeProcessDocuments = useCallback(() => setDocumentsProcess(''), [])

  const [isExporting, setIsExporting] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [showBoardActions, setShowBoardActions] = useState(false)
  const boardActionsRef = useRef(null)

  // Classify and filter prazos
  const classifiedPrazos = useMemo(() => {
    return (prazosCtx.prazos || []).map((p) => {
      const judicial = isJudicialPrazo(p)
      return { ...p, computedStatus: parseStatus(p), isJudicial: judicial }
    })
  }, [prazosCtx.prazos])

  const filteredPrazos = useMemo(() => {
    const source = classifiedPrazos.filter((prazo) => (activeTab === 'judiciais' ? prazo.isJudicial : !prazo.isJudicial))

    return FiltersService.aplicarTodosFiltros(source, {
      area: areaFilter,
      search: searchTerm,
      date: dateFilter,
      responsavel: responsavelFilter,
    })
  }, [classifiedPrazos, activeTab, areaFilter, searchTerm, dateFilter, responsavelFilter])

  const responsavelOptions = useMemo(() => {
    const source = classifiedPrazos.filter((prazo) => (activeTab === 'judiciais' ? prazo.isJudicial : !prazo.isJudicial))
    return Array.from(new Set(source.map((prazo) => (prazo.responsavel || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [classifiedPrazos, activeTab])

  useEffect(() => {
    if (!canViewAgenda && activeTab !== 'judiciais') {
      setActiveTab('judiciais')
    }
  }, [canViewAgenda, activeTab])

  useEffect(() => {
    if (responsavelFilter && !responsavelOptions.includes(responsavelFilter)) {
      setResponsavelFilter('')
    }
  }, [responsavelFilter, responsavelOptions])

  useEffect(() => {
    if (!autoScrollEnabled) return undefined

    const board = document.querySelector('.kanban-board')
    if (!board) return undefined

    const columnBodies = Array.from(board.querySelectorAll('.kanban-column-body'))
    if (!columnBodies.length) return undefined

    const directions = new Map(columnBodies.map((body) => [body, 1]))
    const speed = 1

    const intervalId = window.setInterval(() => {
      columnBodies.forEach((body) => {
        const maxScrollTop = body.scrollHeight - body.clientHeight
        if (maxScrollTop <= 0) return

        const direction = directions.get(body) || 1
        const nextTop = body.scrollTop + direction * speed
        const reachedBottom = nextTop >= maxScrollTop
        const reachedTop = nextTop <= 0

        if (reachedBottom) {
          body.scrollTop = maxScrollTop
          directions.set(body, -1)
          return
        }

        if (reachedTop) {
          body.scrollTop = 0
          directions.set(body, 1)
          return
        }

        body.scrollTop = nextTop
      })
    }, 25)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [autoScrollEnabled, activeTab, filteredPrazos.length])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement))
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  useEffect(() => {
    document.body.classList.toggle('kanban-fullscreen-mode', isFullscreen)

    return () => {
      document.body.classList.remove('kanban-fullscreen-mode')
    }
  }, [isFullscreen])

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (boardActionsRef.current && !boardActionsRef.current.contains(event.target)) {
        setShowBoardActions(false)
      }
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowBoardActions(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const columns = useMemo(
    () => ({
      pendente_analise: filteredPrazos.filter((p) => p.computedStatus === 'pendente_analise'),
      verde: filteredPrazos.filter((p) => p.computedStatus === 'verde'),
      laranja: filteredPrazos.filter((p) => p.computedStatus === 'laranja'),
      vermelho: filteredPrazos.filter((p) => p.computedStatus === 'vermelho'),
      preto: filteredPrazos.filter((p) => p.computedStatus === 'preto'),
      concluido: filteredPrazos.filter((p) => p.computedStatus === 'concluido'),
    }),
    [filteredPrazos]
  )

  const handleSavePrazo = async (data, id) => {
    if (!canManageBoard) return
    if (id) {
      await prazosCtx.updatePrazo(id, data)
    } else {
      await prazosCtx.addPrazo(data)
    }
  }

  const handleDeletePrazo = async (id) => {
    if (!canManageBoard) return
    await prazosCtx.deletePrazo(id)
    notify.showSuccess('Prazo excluído com sucesso!')
    setShowConfirmDelete(null)
  }

  const handleEditPrazo = (prazo) => {
    if (!canManageBoard) return
    setEditingPrazo(prazo)
    setShowPrazoModal(true)
  }

  const handleDrop = async (prazoId, newStatus) => {
    if (!canManageBoard) return
    const patchData = { status: newStatus }
    if (newStatus !== 'concluido') {
      patchData.dataRealizacao = null
      patchData.concluidoEm = null
      patchData.concluidoPor = null
    }
    if (newStatus === 'concluido') {
      patchData.dataRealizacao = new Date().toISOString()
      patchData.concluidoEm = new Date().toISOString()
      patchData.concluidoPor = AuthService.getCurrentUser()?.nome || 'sistema'
    }
    await prazosCtx.updatePrazo(prazoId, patchData)
    notify.showSuccess('Prazo movido!')
  }

  const handleRefresh = async () => {
    if (!isAdmin || isSyncing) return
    setIsSyncing(true)

    try {
      const result = await ApiService.sincronizarLegalManager()

      if (!result.success) {
        notify.showError(result.error || 'Não foi possível sincronizar com o Legal Manager.')
        return
      }

      const updated = prazosCtx.syncPrazosWithResult(result)
      if (!updated) {
        await prazosCtx.loadPrazos()
      }

      notify.showSuccess('Sincronização concluída!')
    } finally {
      setIsSyncing(false)
    }
  }


  const handleExportExcel = async () => {
    setIsExporting(true)

    const result = await ApiService.exportKanbanExcel({
      aba: activeTab,
      area: areaFilter,
      busca: searchTerm,
      data_prazo: dateFilter,
      responsavel: responsavelFilter,
    })

    setIsExporting(false)

    if (!result.success || !result.data) {
      notify.showError(result.error || 'Não foi possível exportar o Kanban para Excel.')
      return
    }

    const blobUrl = window.URL.createObjectURL(result.data)
    const link = document.createElement('a')
    const now = new Date().toISOString().slice(0, 10)
    link.href = blobUrl
    link.download = `kanban-${activeTab}-${now}.xlsx`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(blobUrl)

    notify.showSuccess('Exportação concluída!')
  }

  const handleToggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen()
        return
      }

      await document.documentElement.requestFullscreen()
    } catch (_error) {
      notify.showError('Não foi possível alternar o modo de tela cheia neste navegador.')
    }
  }

  const judicialColumnDefs = [
    { status: 'verde', title: 'WIP', icon: 'fa-check-circle' },
    { status: 'laranja', title: 'Hoje (D-2)', icon: 'fa-exclamation-triangle' },
    { status: 'vermelho', title: 'Atrasados (D-1)', icon: 'fa-times-circle' },
    { status: 'preto', title: 'Fatais Judiciais (D-0)', icon: 'fa-gavel', headerClass: 'fatais_judiciais' },
  ]

  const agendaColumnDefs = [
    { status: 'pendente_analise', title: 'Em Análise', icon: 'fa-hourglass-half' },
    { status: 'concluido', title: 'Concluídos', icon: 'fa-trophy' },
  ]

  const activeColumnDefs = activeTab === 'judiciais' ? judicialColumnDefs : agendaColumnDefs

  useEffect(() => {
    setPageHeaderActions(
      <>
        <div className="search-box">
          <i className="fas fa-search" />
          <input
            type="text"
            placeholder="Pesquisar prazos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {isAdmin && (
          <button
            type="button"
            className="btn btn-outline board-sync-action"
            onClick={handleRefresh}
            disabled={isSyncing}
          >
            <i className={`fas ${isSyncing ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} /> {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        )}
        <div className="board-actions-menu" ref={boardActionsRef}>
          <button
            type="button"
            className={`btn btn-outline board-actions-trigger ${showBoardActions ? 'is-open' : ''}`}
            onClick={() => setShowBoardActions((prev) => !prev)}
            aria-label="Opções do Kanban"
            aria-haspopup="menu"
            aria-expanded={showBoardActions}
          >
            <i className="fas fa-gear" aria-hidden="true" />
          </button>
          {showBoardActions && (
            <div className="board-actions-dropdown" role="menu" aria-label="Opções do Kanban">
              {canManageBoard && (
                <button
                  type="button"
                  className="board-actions-dropdown-item"
                  role="menuitem"
                  onClick={() => {
                    setEditingPrazo(null)
                    setShowPrazoModal(true)
                    setShowBoardActions(false)
                  }}
                >
                  <i className="fas fa-plus" /> Novo Prazo
                </button>
              )}
              <button
                type="button"
                className="board-actions-dropdown-item"
                role="menuitem"
                onClick={() => {
                  handleExportExcel()
                  setShowBoardActions(false)
                }}
                disabled={isExporting}
              >
                <i className="fas fa-file-export" /> {isExporting ? 'Exportando...' : 'Exportar Excel'}
              </button>
              <button
                type="button"
                className="board-actions-dropdown-item"
                role="menuitem"
                onClick={() => {
                  handleToggleFullscreen()
                  setShowBoardActions(false)
                }}
              >
                <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`} /> {isFullscreen ? 'Sair do Fullscreen' : 'Tela cheia'}
              </button>
            </div>
          )}
        </div>
      </>,
    )

    return () => setPageHeaderActions(null)
  }, [
    canManageBoard,
    handleExportExcel,
    handleRefresh,
    handleToggleFullscreen,
    isExporting,
    isFullscreen,
    isAdmin,
    isSyncing,
    searchTerm,
    showBoardActions,
    setPageHeaderActions,
  ])

  return (
    <div className={`board-page ${isFullscreen ? 'is-fullscreen' : ''}`}>
      {/* Page Header */}
      <div className="main-content">
        {isFullscreen && <PjeQuickCopyButton variant="fullscreen" />}
        {isSyncing && (
          <div className="page-loading-overlay" role="status" aria-live="polite">
            <div className="loading-spinner">
              <div className="spinner" />
              <p>Sincronizando dados, aguarde...</p>
            </div>
          </div>
        )}

        {/* 
        <div className="kanban-tabs">
          <button className={`nav-btn ${activeTab === 'judiciais' ? 'active' : ''}`} onClick={() => setActiveTab('judiciais')}>
            <i className="fas fa-gavel" />
            <span>Judiciais</span>
          </button>
          {canViewAgenda && (
            <button className={`nav-btn ${activeTab === 'agenda' ? 'active' : ''}`} onClick={() => setActiveTab('agenda')}>
              <i className="fas fa-calendar-alt" />
              <span>Agenda</span>
            </button>
          )}
        </div>*/}

        {/* Filters */}
        <div className="filters-section filters-section-collapsible">
          <button
            type="button"
            className="filters-collapse-btn"
            onClick={() => setShowFilters((prev) => !prev)}
            aria-expanded={showFilters}
            aria-controls="board-filters"
          >
            <span>
              <i className="fas fa-filter" /> Filtros do board
            </span>
            <span className="filters-collapse-meta">
              Total: {filteredPrazos.length} prazos
              <i className={`fas ${showFilters ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
            </span>
          </button>

          <div id="board-filters" className={`filters-collapsible-content ${showFilters ? 'is-open' : ''}`}>
            <div className="filter-group">
              <label>Área:</label>
              <select value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
                <option value="">Todas as áreas</option>
                {Object.entries(APP_CONFIG.AREAS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Responsável:</label>
              <select value={responsavelFilter} onChange={(e) => setResponsavelFilter(e.target.value)}>
                <option value="">Todos os responsáveis</option>
                {responsavelOptions.map((responsavel) => (
                  <option key={responsavel} value={responsavel}>
                    {responsavel}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Data do Prazo:</label>
              <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
            </div>
            <button type="button" className="btn btn-outline" onClick={() => setAutoScrollEnabled((prev) => !prev)}>
              <i className={`fas ${autoScrollEnabled ? 'fa-pause' : 'fa-play'}`} />{' '}
              {autoScrollEnabled ? 'Pausar rolagem' : 'Play rolagem'}
            </button>
          </div>
        </div>

        {/* Kanban Board */}
        <div className="kanban-board portrait" style={{ ['--kanban-columns']: activeColumnDefs.length }}>
          {activeColumnDefs.map((col) => (
            <KanbanColumn
              key={col.status}
              status={col.status}
              headerClass={col.headerClass}
              title={col.title}
              icon={col.icon}
              cards={columns[col.sourceStatus || col.status] || []}
              loading={prazosCtx.loading}
              onEditCard={handleEditPrazo}
              onDeleteCard={(id) => setShowConfirmDelete(id)}
              onDrop={handleDrop}
              canManageBoard={canManageBoard}
              canViewProcessDocuments={canAccessBoard}
              onViewDocuments={setDocumentsProcess}
            />
          ))}
        </div>
      </div>

      {/* Modals */}
      {canAccessBoard && documentsProcess && <ProcessDocumentsModal isOpen numeroProcesso={documentsProcess} onClose={closeProcessDocuments} />}
      {canManageBoard && (
        <PrazoModal
          isOpen={showPrazoModal}
          onClose={() => {
            setShowPrazoModal(false)
            setEditingPrazo(null)
          }}
          prazo={editingPrazo}
          onSave={handleSavePrazo}
        />
      )}
      {canManageBoard && (
        <ImportModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} existingPrazos={prazosCtx.prazos} />
      )}
      <ConfirmModal
        isOpen={canManageBoard && !!showConfirmDelete}
        onClose={() => setShowConfirmDelete(null)}
        title="Excluir Prazo"
        message="Tem certeza que deseja excluir este prazo? Esta ação não pode ser desfeita."
        onConfirm={() => handleDeletePrazo(showConfirmDelete)}
      />
    </div>
  )
}
